import { LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback, useEffect } from 'react';

import type { FetchItemSummariesByCatalogIdQuery } from '@/redux/modules/item/summary/itemSummary.api';

import CurrentItemLink from '@/components/CatalogPageConsole/components/CurrentItemLink/CurrentItemLink';
import ConsoleSocialBadge from '@/components/ConsoleSocial/ConsoleSocialBadge';
import usePubNubCatalog from '@/components/LiveEventSubscribers/hooks/usePubNubCatalog';
import MessageScroller from '@/pages/BiddingConsole/components/MessageScroller/MessageScroller';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogAbsenteeBidders,
    getCatalogHasVideo,
    getCatalogIsCatalogOnly,
    getCatalogStartTime,
    getCatalogStatus,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchCatalogItemSummaries } from '@/redux/modules/catalog/items/catalogItems.actions';
import { getLiveCatalogBidders, getLiveCatalogCurrentItemId, getLiveCatalogPaused } from '@/redux/modules/console';
import { fetchItemSummaryIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import {
    getItemSummaryFirstImageUrl,
    getItemSummaryIndex,
    getItemSummariesForCatalog,
    getItemSummaryPromoted,
    getItemSummarySellerId,
    getItemSummaryTitle,
    getItemSummaryUrl,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { emptyItemSummary } from '@/types/item/ItemSummary';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { shouldShowLive } from '@/utils/live';
import TEST_HOUSES from '@/utils/testHouses';

import { CoverLotImage, LARGE_IMAGE_SIZE, THUMB_IMAGE_SIZE } from '../../CoverLotImage';

type Props = {
    catalogId: number;
};

const MESSAGE_SCROLLER_Z_INDEX = 2;
const MESSAGE_SCROLLER_MAX_MESSAGES = 2;

export const LiveImageCluster = ({ catalogId }: Props) => {
    const formatMessage = useAppFormatMessage();
    const trackItemCardClicked = useTrackItemCardClicked();
    const dispatch = useAppDispatch();

    const hasVideo = useAppSelector((state) => getCatalogHasVideo(state, catalogId));
    const auctionPaused = useAppSelector((state) => getLiveCatalogPaused(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId));
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId));
    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);

    const items = useAppSelector((state) => getItemSummariesForCatalog(state, catalogId));

    const itemId = currentItem || items?.[0]?.itemId || 0;

    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const itemIndex = index || 0;
    const viewers = bidders + absenteeBidders || 1;

    const nextItem = items.find((i) => i.index === itemIndex + 1) || emptyItemSummary;

    const nextItemTitle = useAppSelector((state) => getItemSummaryTitle(state, nextItem.itemId));
    const nextItemUrl = useAppSelector((state) => getItemSummaryUrl(state, nextItem.itemId));
    const nextItemImageUrlForLog = useAppSelector((state) => getItemSummaryFirstImageUrl(state, nextItem.itemId));

    usePubNubCatalog({
        catalogId,
        subscribeToPresence: showLive,
    });

    useEffect(() => {
        if (showLive) {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
            dispatch(fetchCatalogPresence(catalogId));
        }
    }, [catalogId, dispatch, showLive, sellerId]);

    useEffect(() => {
        // we will load the data for the items in the console
        if (showLive && itemId > 0) {
            const query: FetchItemSummariesByCatalogIdQuery = { index, pageSize: 4 };
            if (TEST_HOUSES.includes(sellerId)) {
                query.preview = true;
            }
            // fetch the currently active item card model
            dispatch(
                fetchItemSummaryIfNeeded({
                    identifier: 'catalog-console',
                    itemId,
                })
            );
            // fetch the next few
            dispatch(fetchCatalogItemSummaries({ catalogId, query }));
        }
    }, [catalogId, index, dispatch, showLive, sellerId, itemId]);

    const handleItemClick = useCallback(
        (clickedItemId: number) => {
            trackItemCardClicked({
                catalogId,
                itemId: clickedItemId,
                promoted,
                source: ItemCardSource.CatalogPageConsole,
            });
        },
        [catalogId, promoted, trackItemCardClicked]
    );

    return (
        <div className="flex h-[280px] gap-xs overflow-hidden rounded-md">
            <div className="relative h-full w-[280px] flex-shrink-0 overflow-hidden rounded-md border border-border-secondary bg-background-primary">
                <div
                    className="h-full w-full"
                    onClick={() => {
                        handleItemClick(itemId);
                    }}
                >
                    <CurrentItemLink
                        catalogId={catalogId}
                        imageWidth={LARGE_IMAGE_SIZE}
                        itemId={itemId}
                        showLive
                    />
                </div>
                <ConsoleSocialBadge
                    className="absolute left-xs top-xs"
                    hasVideo={hasVideo}
                    pause={auctionPaused}
                    viewers={viewers}
                />
                <MessageScroller
                    catalogId={catalogId}
                    className="mx-2xs"
                    convert
                    hideMessages
                    maxMessages={MESSAGE_SCROLLER_MAX_MESSAGES}
                    overlay
                    smallConsole
                    zIndex={MESSAGE_SCROLLER_Z_INDEX}
                />
            </div>
            {nextItem.itemId > 0 && (
                <div className="flex w-[96px] flex-shrink-0 flex-col items-center gap-xs self-start">
                    <div className="aspect-square w-full overflow-hidden rounded-md border border-border-secondary bg-background-primary">
                        <CoverLotImage
                            alt={nextItemTitle}
                            catalogId={catalogId}
                            href={nextItemUrl}
                            imageSize={THUMB_IMAGE_SIZE}
                            itemId={nextItem.itemId}
                            promoted={false}
                            src={nextItemImageUrlForLog}
                            trackItemCardClicked={trackItemCardClicked}
                        />
                    </div>
                    <LabelMini className="w-full text-center uppercase tracking-widest text-text-primary">
                        {formatMessage(['up_next'])}
                    </LabelMini>
                </div>
            )}
        </div>
    );
};
